import { useEffect, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Database, Key, RefreshCw, Search, Table2 } from 'lucide-react';
import { platformFetch } from '../../platform/http';
import { getImageJobFieldMetadata, sortImageJobFields } from './imageJobFieldMetadata';

interface DbSchemaColumn {
  name: string;
  type: string;
  notNull: boolean;
  defaultValue: string | null;
  isPrimaryKey: boolean;
}

interface DbSchemaTable {
  name: string;
  rowCount: number;
  columns: DbSchemaColumn[];
}

const SAMPLE_LIMIT = 10;

function formatRowCount(count: number): string {
  return count.toLocaleString('zh-CN');
}

/** 单元格展示：NULL 灰显，长文本截断并用 title 保留完整内容 */
function renderCellValue(value: unknown) {
  if (value === null || value === undefined) {
    return <span className="text-light-muted italic">NULL</span>;
  }
  if (typeof value === 'boolean') {
    return <span className="font-mono text-light-text">{String(value)}</span>;
  }
  const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return (
    <span className="block max-w-[240px] truncate font-mono text-light-text" title={text}>
      {text}
    </span>
  );
}

export default function DatabaseSchemaTab() {
  const [tables, setTables] = useState<DbSchemaTable[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [sampleRows, setSampleRows] = useState<Array<Record<string, unknown>>>([]);
  const [sampleTotal, setSampleTotal] = useState(0);
  const [samplePage, setSamplePage] = useState(1);
  const [sampleLoading, setSampleLoading] = useState(false);
  const [sampleError, setSampleError] = useState('');
  const [sampleTick, setSampleTick] = useState(0);

  const fetchSchema = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await platformFetch('/admin/db-schema');
      if (!response.ok) throw new Error('获取数据库结构失败');
      const data = await response.json();
      const nextTables: DbSchemaTable[] = data.tables || [];
      setTables(nextTables);
      setSampleTick((tick) => tick + 1);
      setSelectedTable((prev) => {
        const names = nextTables.map((table) => table.name);
        if (prev && names.includes(prev)) return prev;
        return names[0] || null;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取数据库结构失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSchema();
  }, [fetchSchema]);

  // 选中表、翻页或手动刷新时懒加载最新数据样本
  useEffect(() => {
    if (!selectedTable) {
      setSampleRows([]);
      setSampleTotal(0);
      setSampleError('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setSampleLoading(true);
        setSampleError('');
        const response = await platformFetch(
          `/admin/db-schema/${selectedTable}/sample?limit=${SAMPLE_LIMIT}&page=${samplePage}`
        );
        if (!response.ok) throw new Error('获取数据样本失败');
        const data = await response.json();
        if (!cancelled) {
          setSampleRows(data.rows || []);
          setSampleTotal(Number(data.total) || 0);
        }
      } catch (err) {
        if (!cancelled) {
          setSampleRows([]);
          setSampleTotal(0);
          setSampleError(err instanceof Error ? err.message : '获取数据样本失败');
        }
      } finally {
        if (!cancelled) setSampleLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedTable, samplePage, sampleTick]);

  if (loading && tables.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-light-primary" />
      </div>
    );
  }

  if (error && tables.length === 0) {
    return (
      <div className="text-center py-20">
        <p className="text-red-500 mb-4">{error}</p>
        <button
          onClick={fetchSchema}
          className="px-4 py-2 bg-light-primary text-white rounded-xl hover:bg-light-primary/90 transition-colors"
        >
          重试
        </button>
      </div>
    );
  }

  const keyword = search.trim().toLowerCase();
  const filteredTables = keyword
    ? tables.filter((table) => table.name.toLowerCase().includes(keyword))
    : tables;
  const activeTable = tables.find((table) => table.name === selectedTable) || null;
  const isImageJobsTable = activeTable?.name === 'image_jobs';
  const displayColumns = activeTable
    ? isImageJobsTable
      ? sortImageJobFields(activeTable.columns)
      : activeTable.columns
    : [];

  return (
    <div className="space-y-6">
      {/* 顶部工具栏 */}
      <div className="bg-white rounded-2xl p-4 shadow-lg border border-light-border">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-2">
            <Database className="w-5 h-5 text-light-muted" />
            <h3 className="font-semibold text-light-text">数据库结构预览</h3>
            <span className="px-2.5 py-1 rounded-lg bg-light-hover text-xs font-medium text-light-muted">
              {tables.length} 张表
            </span>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative">
              <Search className="w-4 h-4 text-light-muted absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="搜索表名..."
                className="w-full sm:w-64 pl-9 pr-3 py-2 rounded-xl border border-light-border text-sm text-light-text placeholder:text-light-muted focus:outline-none focus:ring-2 focus:ring-light-primary/20"
              />
            </div>
            <button
              onClick={fetchSchema}
              disabled={loading}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-light-hover text-light-text rounded-xl hover:bg-light-border transition-colors text-sm disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              刷新
            </button>
          </div>
        </div>
      </div>

      {/* 主体：左侧表列表 + 右侧结构详情 */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* 表列表：完整展开显示所有表，随页面滚动 */}
        <div className="lg:col-span-5 bg-white rounded-2xl shadow-lg border border-light-border overflow-hidden">
          <div className="px-5 py-4 border-b border-light-border flex items-center justify-between">
            <span className="text-sm font-semibold text-light-text">表列表</span>
            <span className="text-xs text-light-muted">
              {keyword ? `${filteredTables.length} / ${tables.length}` : `${tables.length} 张`}
            </span>
          </div>
          <div>
            {filteredTables.length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-light-muted">没有匹配的表</div>
            ) : (
              filteredTables.map((table) => {
                const isActive = table.name === selectedTable;
                return (
                  <button
                    key={table.name}
                    onClick={() => {
                      setSelectedTable(table.name);
                      setSamplePage(1);
                    }}
                    className={`w-full flex items-center justify-between gap-3 px-5 py-3 text-left border-b border-light-border/50 last:border-b-0 transition-colors ${
                      isActive ? 'bg-light-primary/10' : 'hover:bg-light-hover/60'
                    }`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <Table2
                        className={`w-4 h-4 shrink-0 ${isActive ? 'text-light-primary' : 'text-light-muted'}`}
                      />
                      <span
                        className={`font-mono text-sm break-all text-left ${isActive ? 'text-light-primary font-medium' : 'text-light-text'}`}
                      >
                        {table.name}
                      </span>
                    </span>
                    <span
                      className={`shrink-0 px-2 py-0.5 rounded-md text-xs font-mono ${
                        isActive ? 'bg-light-primary/15 text-light-primary' : 'bg-light-hover text-light-muted'
                      }`}
                    >
                      {formatRowCount(table.rowCount)}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* 结构详情 */}
        <div className="lg:col-span-7 space-y-6">
          {!activeTable ? (
            <div className="bg-white rounded-2xl shadow-lg border border-light-border py-20 text-center text-sm text-light-muted">
              请从左侧选择一张表查看结构
            </div>
          ) : (
            <>
              {/* 列结构 */}
              <div className="bg-white rounded-2xl shadow-lg border border-light-border overflow-hidden">
                <div className="px-5 py-4 border-b border-light-border flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <Table2 className="w-4 h-4 text-light-primary shrink-0" />
                    <span className="font-mono text-sm font-semibold text-light-text truncate">
                      {activeTable.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="px-2.5 py-1 rounded-lg bg-light-hover text-xs font-medium text-light-muted">
                      {formatRowCount(activeTable.rowCount)} 行
                    </span>
                    <span className="px-2.5 py-1 rounded-lg bg-light-hover text-xs font-medium text-light-muted">
                      {activeTable.columns.length} 列
                    </span>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-light-hover text-left text-sm text-light-muted">
                        <th className="px-5 py-3 font-medium whitespace-nowrap">列名</th>
                        {isImageJobsTable && (
                          <>
                            <th className="px-5 py-3 font-medium whitespace-nowrap">分类</th>
                            <th className="px-5 py-3 font-medium min-w-[320px]">字段说明</th>
                          </>
                        )}
                        <th className="px-5 py-3 font-medium whitespace-nowrap">类型</th>
                        <th className="px-5 py-3 font-medium whitespace-nowrap">主键</th>
                        <th className="px-5 py-3 font-medium whitespace-nowrap">非空</th>
                        <th className="px-5 py-3 font-medium whitespace-nowrap">默认值</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-light-border">
                      {displayColumns.map((column) => {
                        const metadata = isImageJobsTable
                          ? getImageJobFieldMetadata(column.name)
                          : undefined;
                        return (
                          <tr key={column.name} className="hover:bg-light-hover/50 transition-colors">
                            <td className="px-5 py-3 text-sm whitespace-nowrap">
                              <span className="flex items-center gap-1.5">
                                {column.isPrimaryKey && (
                                  <Key className="w-3.5 h-3.5 text-orange-500 shrink-0" />
                                )}
                                <span className="font-mono font-medium text-light-text">{column.name}</span>
                              </span>
                            </td>
                            {isImageJobsTable && (
                              <>
                                <td className="px-5 py-3 text-sm whitespace-nowrap">
                                  <span className="inline-flex px-2 py-1 rounded-md bg-light-primary/10 text-light-primary text-xs font-medium">
                                    {metadata?.category ?? '其他字段'}
                                  </span>
                                </td>
                                <td className="px-5 py-3 min-w-[320px] max-w-[440px] text-sm leading-6 text-light-muted whitespace-normal">
                                  {metadata?.description ?? '尚未配置用途说明，请结合字段名和业务数据确认。'}
                                </td>
                              </>
                            )}
                            <td className="px-5 py-3">
                              <span className="px-2 py-0.5 rounded-md bg-light-hover text-xs font-mono text-light-muted">
                                {column.type}
                              </span>
                            </td>
                            <td className="px-5 py-3 text-sm text-light-text">
                              {column.isPrimaryKey ? '是' : '-'}
                            </td>
                            <td className="px-5 py-3 text-sm text-light-text">
                              {column.notNull ? '是' : '否'}
                            </td>
                            <td className="px-5 py-3 text-xs font-mono text-light-muted">
                              {column.defaultValue ?? '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 数据样本 */}
              <div className="bg-white rounded-2xl shadow-lg border border-light-border overflow-hidden">
                <div className="px-5 py-4 border-b border-light-border flex flex-wrap items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-light-text">数据样本（最新优先）</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSamplePage((page) => Math.max(1, page - 1))}
                      disabled={sampleLoading || samplePage <= 1}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-light-hover text-light-text hover:bg-light-border transition-colors text-xs disabled:opacity-40"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                      上一页
                    </button>
                    <span className="text-xs text-light-muted whitespace-nowrap">
                      第 {samplePage} / {Math.max(1, Math.ceil(sampleTotal / SAMPLE_LIMIT))} 页 · 共{' '}
                      {formatRowCount(sampleTotal)} 行
                    </span>
                    <button
                      onClick={() =>
                        setSamplePage((page) =>
                          Math.min(Math.max(1, Math.ceil(sampleTotal / SAMPLE_LIMIT)), page + 1)
                        )
                      }
                      disabled={
                        sampleLoading || samplePage >= Math.max(1, Math.ceil(sampleTotal / SAMPLE_LIMIT))
                      }
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-light-hover text-light-text hover:bg-light-border transition-colors text-xs disabled:opacity-40"
                    >
                      下一页
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setSampleTick((tick) => tick + 1)}
                      disabled={sampleLoading}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-light-hover text-light-text hover:bg-light-border transition-colors text-xs disabled:opacity-50"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${sampleLoading ? 'animate-spin' : ''}`} />
                      重新加载
                    </button>
                  </div>
                </div>
                {sampleError ? (
                  <div className="px-5 py-10 text-center text-sm text-red-500">{sampleError}</div>
                ) : sampleLoading ? (
                  <div className="px-5 py-10 text-center text-sm text-light-muted">加载中...</div>
                ) : sampleRows.length === 0 ? (
                  <div className="px-5 py-10 text-center text-sm text-light-muted">
                    {sampleTotal > 0 ? '当前页没有数据' : '该表暂无数据'}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-light-hover text-left text-sm text-light-muted">
                          {displayColumns.map((column) => (
                            <th key={column.name} className="px-4 py-3 font-medium whitespace-nowrap">
                              <span className="flex items-center gap-1">
                                {column.isPrimaryKey && (
                                  <Key className="w-3 h-3 text-orange-500 shrink-0" />
                                )}
                                <span className="font-mono">{column.name}</span>
                              </span>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-light-border">
                        {sampleRows.map((row, rowIndex) => (
                          <tr key={rowIndex} className="hover:bg-light-hover/50 transition-colors">
                            {displayColumns.map((column) => (
                              <td key={column.name} className="px-4 py-2.5 text-xs whitespace-nowrap">
                                {renderCellValue(row[column.name])}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
